import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import "../../Components/report.css";
import { Link } from "react-router-dom";
import StickyHeader from "../../../components/StickyHeader";
import "./index.css";

function SprintGrades(props) {
  const [isExtraCreditMode, setIsExtraCreditMode] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isLoading = props.sprintGrades.length === 0;

  /**
   *
   * @param {boolean} extraCreditMode - If true, shows extra work as extra credit. If false, scales grades to required work only.
   * @returns {Array} - An array of student grades
   */
  const calculateStudentGrades = (extraCreditMode = false) => {
    const assignments = props.assignments.filter(
      (assignment) => assignment.sprint === props.sprint._id
    );
    const students = props.teacherStudents
      .filter((student) =>
        student.classes.some((classId) => classId === props.sprint.class)
      )
      .sort((a, b) => {
        if (a.lastName < b.lastName) return -1;
        if (a.lastName > b.lastName) return 1;
        if (a.firstName < b.firstName) return -1;
        if (a.firstName > b.firstName) return 1;
        return 0;
      });
    const evaluations = props.sprintGrades;

    return students.map((student) => {
      let studentTotalGrade = 0;
      let totalPossible = 0;
      let studentDidExtraWork = false;

      const assignmentGrades = assignments.map((assignment) => {
        const studentEvaluations = evaluations.filter((evaluation) => {
          return (
            evaluation.assignment === assignment._id &&
            evaluation.user === student._id
          );
        });

        let studentGrade = 0;
        let assignmentPossible = assignment.evaluationsRequired * 3;
        totalPossible += assignmentPossible;
        let studentPossible = 0;
        let studentDidExtraWorkForThisAssignment = false;

        for (let i = 0; i < studentEvaluations.length; i++) {
          if (studentEvaluations[i].teacherGrade) {
            studentGrade += studentEvaluations[i].teacherGrade;
          }
          studentPossible += 3;
        }

        //
        // If extraCreditMode is false: scale grades to required work only
        // If the student completed MORE evaluations than required for this assignment,
        // and they've received teacher grades for at least one of them, rescale their grade.
        // The studentGrade represents the sum of teacher-assigned grades for this student's evaluations on this assignment.
        // Normally, their possible points is evaluationsRequired * 3 (3 points per required evaluation by assignment design).
        // But if the student did extra evaluations, their "studentPossible" is higher.
        // To prevent extra (bonus) work from "watering down" their grade,
        // we scale studentGrade up proportionally so it's out of the full assignmentPossible.
        if (studentEvaluations.length > assignment.evaluationsRequired) {
          if (extraCreditMode) {
            // If extraCreditMode is true: show all work done, including extra evaluations
            studentDidExtraWork = true;
            studentDidExtraWorkForThisAssignment = true;
          } else if (studentGrade !== 0 && studentPossible !== 0) {
            // Scale up grade so overwork doesn't lower a student's percentage
            studentGrade *= assignmentPossible / studentPossible;
          }
        }

        studentTotalGrade += studentGrade;

        return {
          assignmentId: assignment._id,
          assignmentName: assignment.name,
          studentGrade,
          assignmentPossible,
          studentDidExtraWorkForThisAssignment,
        };
      });

      // Format total grade to 2 decimal places if needed
      let formattedTotalGrade = studentTotalGrade;
      if (studentTotalGrade % 1 !== 0) {
        formattedTotalGrade = parseFloat(studentTotalGrade.toFixed(2));
      }

      return {
        student: {
          _id: student._id,
          firstName: student.firstName,
          lastName: student.lastName,
        },
        assignmentGrades,
        totalGrade: formattedTotalGrade,
        totalPossible,
        studentDidExtraWork,
      };
    });
  };

  const getExportData = () => {
    const assignments = props.assignments.filter(
      (a) => a.sprint === props.sprint._id
    );
    const data = calculateStudentGrades(isExtraCreditMode);
    const headers = [
      "Student",
      ...assignments.map((a) => a.name),
      "Total Grade",
    ];
    const rows = data.map((d) => [
      `${d.student.lastName}, ${d.student.firstName}`,
      ...d.assignmentGrades.map(
        (g) => `${g.studentGrade} / ${g.assignmentPossible}`
      ),
      `${d.totalGrade} / ${d.totalPossible}`,
    ]);
    return { headers, rows };
  };

  const downloadCSV = () => {
    const { headers, rows } = getExportData();
    const csvRows = [headers, ...rows].map((row) =>
      row
        .map((cell) => (String(cell).includes(",") ? `"${cell}"` : cell))
        .join(",")
    );
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `grades-${props.sprint.name || props.sprint._id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadXLSX = () => {
    const { headers, rows } = getExportData();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Grades");
    XLSX.writeFile(wb, `grades-${props.sprint.name || props.sprint._id}.xlsx`);
  };

  const mappedStudentGrades = () => {
    const assignments = props.assignments.filter(
      (assignment) => assignment.sprint === props.sprint._id
    );
    const studentGradesData = calculateStudentGrades(isExtraCreditMode);

    return (
      <table className="gradeTable">
        <tr className="header">
          <th>Students</th>
          {assignments.map((assignment, index) => {
            return <th key={index}>{assignment.name}</th>;
          })}
          <th>Total Grade</th>
        </tr>
        {studentGradesData.map((studentData, index) => {
          return (
            <tr
              key={index}
              className={
                studentData.studentDidExtraWork ? "extraCreditRow" : ""
              }
            >
              <td>{`${studentData.student.lastName}, ${studentData.student.firstName}`}</td>
              {studentData.assignmentGrades.map((gradeData, gradeIndex) => (
                <td
                  key={gradeIndex}
                  className={gradeData.studentDidExtraWorkForThisAssignment ? "extraCreditCell" : ""}
                >{`${gradeData.studentGrade} / ${gradeData.assignmentPossible}`}</td>
              ))}
              <td>{`${studentData.totalGrade} / ${studentData.totalPossible}`}</td>
            </tr>
          );
        })}
      </table>
    );
  };

  return (
    <div className="reportPage">
      <div className="backButton">
        <Link to="/teacher">
          <img src={require("../../../Images/leftArrow.png")} alt="<-" />
          Return to Dashboard
        </Link>
      </div>
      {isLoading && <div className="loading">Loading...</div>}
      {!isLoading && (
        <div>
          <StickyHeader>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h1>Student Grades</h1>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div className="downloadButtons">
                  <button
                    className="downloadBtn"
                    onClick={downloadCSV}
                  >
                    Download CSV
                  </button>
                  <button
                    className="downloadBtn"
                    onClick={downloadXLSX}
                  >
                    Download XLSX
                  </button>
                </div>
                <button
                  onClick={() => setIsExtraCreditMode(!isExtraCreditMode)}
                  style={{
                    padding: "10px 20px",
                    fontSize: "14px",
                    cursor: "pointer",
                    backgroundColor: isExtraCreditMode ? "#4CAF50" : "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontWeight: "bold",
                  }}
                >
                  {isExtraCreditMode ? "Extra Credit View" : "Normal View"}
                </button>
              </div>
            </div>
          </StickyHeader>
          {mappedStudentGrades()}
        </div>
      )}
    </div>
  );
}

export default connect((state) => state.root, {})(SprintGrades);
